import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { Reveal } from "@/components/reveal";
import { ArrowRight, ChevronRight, Sparkles } from "lucide-react";

const TITLE = "The creator's guide to affiliate commissions";
const DESCRIPTION =
  "How affiliate rates actually work in 2026 — tracking windows, attribution models, and the math behind a sustainable creator income.";
const PATH = "/journal/creators-guide-to-affiliate-commissions";

export const Route = createFileRoute("/journal_/creators-guide-to-affiliate-commissions")({
  head: () => ({
    meta: [
      { title: `${TITLE} — Pluggz` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "Pluggz" },
          publisher: {
            "@type": "Organization",
            name: "Pluggz",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
          },
          mainEntityOfPage: `${SITE_URL}${PATH}`,
          datePublished: "2026-06-10",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}/journal` },
            { "@type": "ListItem", position: 3, name: TITLE, item: `${SITE_URL}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: ArticlePage,
});

function ArticlePage() {
  return (
    <article className="relative">
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-accent/20 blur-3xl float-slow" />

      <nav aria-label="Breadcrumb" className="relative mx-auto max-w-3xl px-6 pt-10">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <li><Link to="/" className="hover:text-foreground transition-colors">Home</Link></li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li><Link to="/journal" className="hover:text-foreground transition-colors">Journal</Link></li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li aria-current="page" className="text-foreground">Affiliate commissions</li>
        </ol>
      </nav>

      <header className="relative mx-auto max-w-3xl px-6 pt-10 pb-10 text-center">
        <Reveal>
          <p className="editorial-eyebrow inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Guide · 8 min read
          </p>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">
            The creator's guide to <span className="italic shimmer">affiliate commissions</span>
          </h1>
        </Reveal>
        <Reveal delay={260}>
          <p className="mt-6 text-sm text-muted-foreground">By the Pluggz Editorial Team · June 2026</p>
        </Reveal>
      </header>

      <Reveal>
        <div className="relative mx-auto mb-12 max-w-5xl px-6">
          <div className="aspect-[16/7] w-full overflow-hidden rounded-3xl bg-gradient-to-br from-accent/30 via-card to-background ring-1 ring-border/60" />
        </div>
      </Reveal>

      <div className="mx-auto max-w-2xl px-6 pb-24 font-light text-foreground/90">
        <section className="prose-spacing space-y-6 leading-relaxed">
          <p className="text-lg">
            Affiliate commission is the quietest, most misunderstood line in a creator's income statement. It is also, for most working creators in 2026, the largest. Brand deals get the headlines; affiliate pays the rent. This is a plain-English guide to how it actually works — the rates, the tracking, the windows, and the maths you need to plan around.
          </p>

          <h2 className="mt-12 font-serif text-3xl">What a commission actually is</h2>
          <p className="text-muted-foreground">
            A commission is a percentage of the sale price that a brand pays you when a shopper buys something through your link. It is paid <em>after</em> the return window closes — usually 14 to 30 days — and almost never on the tax or shipping. The headline rate you see on a creator network is the gross rate; the rate you'll bank is roughly 80% of that, once returns and ineligible categories are netted out.
          </p>

          <h2 className="mt-12 font-serif text-3xl">Typical rates by category</h2>
          <p className="text-muted-foreground">
            Fashion and accessories sit between 5% and 12%. Beauty is usually 8% to 15%, with prestige labels at the higher end. Home goods land at 4% to 10%. Electronics are stingy — often 1% to 4% — because margins are tight. Subscription products, by contrast, can pay 30% to 50% of the first month, which is why software and meal-kit links over-index in creator income.
          </p>

          <h2 className="mt-12 font-serif text-3xl">Tracking windows and why they matter</h2>
          <p className="text-muted-foreground">
            A tracking window is how long a brand will credit you for a sale after the initial click. The industry default is 30 days, but Amazon famously runs a 24-hour window. A short window means a shopper has to buy almost immediately; a long window means a shopper who saves the link for next payday still counts as yours. When you compare networks, the window matters more than the rate.
          </p>

          <h2 className="mt-12 font-serif text-3xl">First-click, last-click, and the attribution problem</h2>
          <p className="text-muted-foreground">
            If a shopper taps your link, leaves, then buys through someone else's link a week later — who gets paid? Most networks default to <em>last-click</em>, which means the last creator in the chain wins. A growing number, including the Pluggz network, support <em>first-click</em> attribution for storefronts: the creator who introduced the brand keeps the commission even if the shopper came back via a different link. First-click is fairer to the creators doing the discovery work; last-click rewards the creators closest to the purchase.
          </p>

          <h2 className="mt-12 font-serif text-3xl">The maths of sustainable income</h2>
          <p className="text-muted-foreground">
            A simple model: 10,000 monthly storefront visits, a 6% product-tap rate, a 4% checkout conversion on tapped products, an average order value of £80, an effective commission rate of 9%. That works out to roughly 24 paid orders per month and around £170 in commission. The lever with the highest return isn't traffic — it's the product-tap rate, which is almost entirely a function of how well-edited your storefront is.
          </p>

          <h2 className="mt-12 font-serif text-3xl">What to watch out for</h2>
          <p className="text-muted-foreground">
            Three things eat commissions silently. First, coupon-site overrides — a shopper who taps your link and then opens a coupon extension is often re-attributed to that extension at checkout. Second, app-to-web breakage — a shopper who taps a link in Instagram, then opens the brand's app, sometimes drops out of tracking entirely. Third, return cycles — if you push a high-return category like fast fashion, your gross commission can look great in week one and shrink by 30% by week six.
          </p>

          <h2 className="mt-12 font-serif text-3xl">A working creator's checklist</h2>
          <p className="text-muted-foreground">
            Always disclose. Pick two or three networks rather than ten — the tax forms alone aren't worth the long tail. Read the terms of service for ineligible categories before you push a product. And every quarter, do a single afternoon's accounting: pull commission, returns, and effective rate per brand. The pattern will tell you which partnerships to deepen and which to quietly let go.
          </p>

          <div className="mt-14 rounded-3xl border border-border/60 bg-gradient-to-br from-card via-background to-card p-8 text-center">
            <p className="editorial-eyebrow">Keep reading</p>
            <h3 className="mt-3 font-serif text-2xl">More from the Journal</h3>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
              <Link to="/journal" className="group inline-flex items-center gap-2 border-b border-foreground pb-1 text-sm tracking-wide">
                Back to the Journal
                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </article>
  );
}